import {
  makeChangeAdminTransactions,
  makeChangeFeeAdminTransactions,
  makeWithdrawPlatformFeesTransactions,
} from "@/lib/clients";
import { cn, formatNumber, shorten } from "@/lib/utils";
import { useWallet } from "@txnlab/use-wallet";
import {
  DSContractListing,
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { useState, Dispatch, SetStateAction } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  ShieldAlert,
  UserPen,
  UserRound,
  Wrench,
} from "lucide-react";
import { AccountLink, ApplicationLink } from "@/components/links";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConnectButton } from "@/components/connect-button";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { cx } from "class-variance-authority";
import { AccountsDropdown } from "@/components/account-sheet";
import { ErrorComponent } from "@/components/error-component";
import { useListings } from "@/hooks/use-listings";
import { clearDSCache } from "@/hooks/use-cstate";

export function AdminCard({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  if (!cState || !listing) return null;
  const [throwError] = useState(false);
  const { activeAddress } = useWallet();
  if (throwError) throw new Error("EC error");
  return (
    <Card className={cx("w-[350px] max-w-[100%] relative")}>
      <CardHeader className="opacity-0 animate-fade-in-4">
        <div className="flex justify-between items-start">
          <div className="flex gap-2 text-3xl">
            <div className="pb-0 font-normal">Admin</div>
            <span className="text-muted-foreground">Panel</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center opacity-0 animate-fade-in-4">
        <div className="flex flex-col items-left w-full">
          <div className="flex w-full justify-between my-1">
            <div className="text-muted-foreground">Your role</div>
            <div>
              {activeAddress === cState.adminAddr ? "Admin" : "Fee admin"}
            </div>
          </div>

          <div className="flex w-full justify-between items-center">
            <div className="text-muted-foreground">Admin</div>
            <div className="flex items-center -mr-2">
              <AccountLink short={true} address={cState.adminAddr} />
              {/* <CopyButton
                noLabel={true}
                buttonVariant="ghost"
                value={cState.adminAddr}
              /> */}
            </div>
          </div>

          <div className="flex w-full justify-between items-center">
            <div className="text-muted-foreground">Fee Admin</div>
            <div className="flex items-center -mr-2">
              <AccountLink short={true} address={cState.feeAdminAddr} />
              {/* <CopyButton
                noLabel={true}
                buttonVariant="ghost"
                value={cState.feeAdminAddr}
              /> */}
            </div>
          </div>

          <div className="flex w-full justify-between my-1">
            <div className="text-muted-foreground">Platform fees</div>
            <div>{formatNumber(cState.platformFees)} ALGO</div>
          </div>
          {/* <div className="flex justify-between">
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => {
                throw new Error(`Uncaught test`);
              }}
            >
              Uncaught Err
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => {
                setThrowError(true);
              }}
            >
              ErrorBoundary Err
            </Button>
          </div> */}
        </div>
      </CardContent>
      <CardFooter className="justify-between opacity-0 animate-fade-in-4">
        <ChangeAdminAccountIndex cState={cState} listing={listing} />
        <PlatformClaimButton cState={cState} listing={listing} />
      </CardFooter>
      <ShieldAlert
        size={160}
        strokeWidth={1.5}
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-10"
      />
    </Card>
  );
}

function PlatformClaimButton({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  const { activeAddress } = useWallet();
  const { refetch } = useListings();

  return (
    <TransactionDialog
      type="operation"
      trigger={(onClick) => (
        <Button
          variant={cState.platformFees === 0n ? "outline" : "default"}
          size="sm"
          onClick={onClick}
          disabled={
            cState.platformFees === 0n || cState.feeAdminAddr !== activeAddress
          }
        >
          <Coins />
          CLAIM
        </Button>
      )}
      onSuccess={() => {
        refetch();
      }}
      transactionBuilder={() =>
        makeWithdrawPlatformFeesTransactions(
          listing.appId,
          activeAddress!,
          Number(cState.platformFees)
        )
      }
      title={`Withdrawing platform fees`}
    >
      {transactionInner(
        "withdrawal",
        <div className="flex flex-col gap-2">
          <div>
            You are going to <strong>withdraw platform fees</strong> from the{" "}
            <span className="font-bold">{listing.lstName}</span> contract
          </div>
        </div>,
        <>
          <div className="flex justify-between gap-2 mb-2">
            <div className="flex items-center">
              <UserRound size={18} className="mr-1" /> Account
            </div>{" "}
            <span>{shorten(activeAddress!)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <div className="flex items-center">
              <ChevronRight size={18} className="mr-1" />
              App Call
            </div>
            <span className="text-right">
              App <ApplicationLink id={listing.appId} />
            </span>
          </div>
          {cState.platformFees !== 0n ? (
            <div className="flex justify-between gap-2">
              <div className="flex items-center ml-2">
                <ChevronLeft size={18} className="mr-1" /> Receiving
              </div>{" "}
              <span className="text-right">
                {formatNumber(cState.platformFees)} ALGO
              </span>
            </div>
          ) : null}
        </>
      )}
    </TransactionDialog>
  );
}

export function ChangeAdminAccountIndex({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  const [mode, setMode] = useState<"" | "feeadmin" | "admin">("");
  const [open, setOpen] = useState(false);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    setMode("");
  };
  const doClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger>
        <Button onClick={() => setOpen(true)} variant="outline" size="sm">
          <Wrench />
          CHANGE ADMINS
        </Button>
      </DialogTrigger>
      <DialogContent>
        {mode === "" ? (
          <ChangeAdminAccountRolePick cState={cState} setMode={setMode} />
        ) : mode === "admin" ? (
          <AdminTransfer doClose={doClose} cState={cState} listing={listing} />
        ) : (
          <FeeAdminTransfer
            doClose={doClose}
            cState={cState}
            listing={listing}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangeAdminAccountRolePick({
  cState,
  setMode,
}: {
  cState: DSContractState;
  setMode: Dispatch<SetStateAction<"" | "feeadmin" | "admin">>;
}) {
  const { activeAddress } = useWallet();
  return (
    <DialogHeader>
      <DialogTitle className="pb-2">Admin role management</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        <div>Select your operation type</div>
        <Button variant="outline" onClick={() => setMode("feeadmin")}>
          Change fee admin
        </Button>
        <Button
          variant="outline"
          disabled={cState.adminAddr !== activeAddress}
          onClick={() => setMode("admin")}
        >
          Change admin
        </Button>
      </DialogDescription>
    </DialogHeader>
  );
}

function FeeAdminTransfer({
  listing,
  doClose,
}: {
  cState: DSContractState;
  listing: DSContractListing;
  doClose: () => void;
}) {
  const { activeAddress } = useWallet();
  const [nextAddress, setNextAddress] = useState("");

  const changeAddress = ({
    currentTarget: { value: valueStr },
  }: React.ChangeEvent<HTMLInputElement>) => setNextAddress(valueStr);

  return (
    <DialogHeader>
      <DialogTitle className="pb-4">Fee Admin role transfer</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        <Label
          className="text-foreground flex justify-between"
          htmlFor="new_feeadmin"
        >
          New fee admin address
        </Label>
        <Input onChange={changeAddress} />

        <TransactionDialog
          type="operation"
          trigger={(onClick) => (
            <ConnectButton
              className={cn(
                "inline-flex w-full items-center justify-center rounded-lg text-sm font-medium",
                "ring-offset-background transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "disabled:pointer-events-none disabled:opacity-50",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "h-12 px-6 py-2",
                "relative"
              )}
              onClick={onClick}
            >
              <UserPen size={18} className="mr-1.5" />
              <span>Change Fee Admin</span>
            </ConnectButton>
          )}
          onSuccess={() => {
            clearDSCache(listing.appId);
            doClose();
          }}
          transactionBuilder={() =>
            makeChangeFeeAdminTransactions(
              listing.appId,
              activeAddress!,
              nextAddress
            )
          }
          title={`Change fee admin`}
        >
          {transactionInner(
            "operation",
            <div className="flex flex-col gap-2">
              <div>
                You are going to <strong>change fee admin role</strong> for the{" "}
                <span className="font-bold">{listing.lstName}</span> contract
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2 text-foreground">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center pl-5">New fee admin</div>
                <span className="text-right  text-foreground">
                  {shorten(nextAddress, 6)}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </DialogDescription>
    </DialogHeader>
  );
}

function AdminTransfer({
  cState,
  listing,
  doClose,
}: {
  cState: DSContractState;
  listing: DSContractListing;
  doClose: () => void;
}) {
  const { activeAddress, connectedActiveAccounts } = useWallet();
  const [nextAddress, setNextAddress] = useState("");

  const addresses =
    connectedActiveAccounts
      ?.filter(({ address: a }) => a !== cState.adminAddr)
      .map(({ address: a }) => a) ?? [];

  const setActive = (newAdmin: string) =>
    setNextAddress(newAdmin?.toUpperCase());

  if (connectedActiveAccounts.length === 1)
    return (
      <DialogHeader>
        <DialogTitle className="pb-4 text-destructive">
          Admin role transfer
        </DialogTitle>
        <DialogDescription className="gap-4 flex flex-col">
          <ErrorComponent error="Error: Only one account connected. You need to sign with both old and new admin accounts." />
        </DialogDescription>
      </DialogHeader>
    );

  return (
    <DialogHeader>
      <DialogTitle className="pb-4 text-destructive">
        Admin role transfer
      </DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        <Label
          className="text-foreground flex justify-between"
          htmlFor="new_admin"
        >
          New admin address
        </Label>
        {/* <Input onChange={changeAddress} /> */}
        <AccountsDropdown
          value={shorten(nextAddress, 12)}
          addresses={addresses}
          setActive={setActive}
        />
        <div className="text-destructive">
          Warning: This operation can not be undone.
        </div>

        <TransactionDialog
          type="operation"
          trigger={(onClick) => (
            <ConnectButton
              className={cn(
                "inline-flex w-full items-center justify-center rounded-lg text-sm font-medium",
                "ring-offset-background transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "disabled:pointer-events-none disabled:opacity-50",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "h-12 px-6 py-2",
                "relative"
              )}
              onClick={onClick}
            >
              <UserPen size={18} className="mr-1.5" />
              <span>Change Admin</span>
            </ConnectButton>
          )}
          onSuccess={() => {
            clearDSCache(listing.appId);
            doClose();
          }}
          transactionBuilder={() =>
            makeChangeAdminTransactions(
              listing.appId,
              activeAddress!,
              nextAddress
            )
          }
          title={`Change admin`}
        >
          {transactionInner(
            "operation",
            <div className="flex flex-col gap-2">
              <div>
                You are going to{" "}
                <strong className="text-destructive">change admin role</strong>{" "}
                for the <span className="font-bold">{listing.lstName}</span>{" "}
                contract
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2 text-foreground">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center pl-5">New admin</div>
                <span className="text-right  text-foreground">
                  {shorten(nextAddress, 6)}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </DialogDescription>
    </DialogHeader>
  );
}
